import { useEffect, useState } from 'react'
import {
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore'
import { db } from '@/lib/firebase'

export interface PlatformDayStat {
  date: string
  steps: number
  avgSteps: number
  sleep: number
  avgSleep: number
  avgHr: number
  avgSpo2: number
  avgStress: number
  activeUsers: number
  workouts: number
}

function formatDate(d: Date): string {
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function usePlatformDailyStats(daysBack = 30) {
  const [data, setData] = useState<PlatformDayStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        const since = new Date()
        since.setDate(since.getDate() - daysBack)
        const sinceStr = formatDate(since)

        const healthQ = query(
          collectionGroup(db, 'health'),
          where('date', '>=', sinceStr),
        )
        const healthSnap = await getDocs(healthQ)

        const byDate = new Map<
          string,
          {
            steps: number; stepsCount: number;
            sleep: number; sleepCount: number;
            hr: number; hrCount: number;
            spo2: number; spo2Count: number;
            stress: number; stressCount: number;
            users: Set<string>;
          }
        >()

        healthSnap.docs.forEach((d) => {
          const data = d.data() as any
          const date = data.date as string | undefined
          if (!date) return
          if (!byDate.has(date)) {
            byDate.set(date, {
              steps: 0, stepsCount: 0,
              sleep: 0, sleepCount: 0,
              hr: 0, hrCount: 0,
              spo2: 0, spo2Count: 0,
              stress: 0, stressCount: 0,
              users: new Set(),
            })
          }
          const bucket = byDate.get(date)!
          const uid = d.ref.parent.parent?.id
          if (uid) bucket.users.add(uid)
          if (typeof data.steps === 'number' && data.steps > 0) {
            bucket.steps += data.steps
            bucket.stepsCount += 1
          }
          if (typeof data.sleepTotal === 'number' && data.sleepTotal > 0) {
            bucket.sleep += data.sleepTotal
            bucket.sleepCount += 1
          }
          const hr = data.heartRateAvg ?? data.heartRate
          if (typeof hr === 'number' && hr > 0) {
            bucket.hr += hr
            bucket.hrCount += 1
          }
          if (typeof data.spo2 === 'number' && data.spo2 > 0) {
            bucket.spo2 += data.spo2
            bucket.spo2Count += 1
          }
          if (typeof data.stress === 'number' && data.stress > 0) {
            bucket.stress += data.stress
            bucket.stressCount += 1
          }
        })

        const workoutByDate = new Map<string, number>()
        try {
          const wq = query(
            collectionGroup(db, 'workoutHistory'),
            where('startTime', '>=', since.getTime()),
          )
          const wsnap = await getDocs(wq)
          wsnap.docs.forEach((d) => {
            const data = d.data() as any
            const ts = typeof data.startTime === 'number' ? data.startTime : null
            if (ts == null) return
            const dateStr = formatDate(new Date(ts))
            workoutByDate.set(dateStr, (workoutByDate.get(dateStr) ?? 0) + 1)
          })
        } catch (e) {
          console.warn('[usePlatformDailyStats] workout aggregation skipped:', e)
        }

        const stats: PlatformDayStat[] = Array.from(byDate.entries())
          .map(([date, b]) => ({
            date,
            steps: b.steps,
            avgSteps: b.stepsCount > 0 ? Math.round(b.steps / b.stepsCount) : 0,
            sleep: b.sleep,
            avgSleep: b.sleepCount > 0 ? Math.round(b.sleep / b.sleepCount) : 0,
            avgHr: b.hrCount > 0 ? Math.round(b.hr / b.hrCount) : 0,
            avgSpo2: b.spo2Count > 0 ? Math.round(b.spo2 / b.spo2Count) : 0,
            avgStress: b.stressCount > 0 ? Math.round(b.stress / b.stressCount) : 0,
            activeUsers: b.users.size,
            workouts: workoutByDate.get(date) ?? 0,
          }))
          .sort((a, b) => a.date.localeCompare(b.date))

        if (!cancelled) {
          setData(stats)
          setError(null)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[usePlatformDailyStats] error:', err)
          setError(err instanceof Error ? err : new Error('Aggregation failed'))
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [daysBack])

  return { data, loading, error }
}

export interface LeaderboardEntry {
  uid: string
  displayName: string
  photoUrl: string | null
  email: string | null
  totalSteps: number
  daysActive: number
  avgSteps: number
  rank: number
}

export function useStepsLeaderboard(daysBack = 7, topN = 10) {
  const [data, setData] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        setLoading(true)
        const since = new Date()
        since.setDate(since.getDate() - daysBack)
        const sinceStr = formatDate(since)

        const healthQ = query(
          collectionGroup(db, 'health'),
          where('date', '>=', sinceStr),
        )
        const healthSnap = await getDocs(healthQ)

        const byUid = new Map<string, { totalSteps: number; days: Set<string> }>()
        healthSnap.docs.forEach((d) => {
          const data = d.data() as any
          const steps = typeof data.steps === 'number' ? data.steps : 0
          if (steps <= 0) return
          const uid = d.ref.parent.parent?.id
          if (!uid) return
          if (!byUid.has(uid)) byUid.set(uid, { totalSteps: 0, days: new Set() })
          const bucket = byUid.get(uid)!
          bucket.totalSteps += steps
          if (data.date) bucket.days.add(data.date)
        })

        const sorted = Array.from(byUid.entries())
          .sort((a, b) => b[1].totalSteps - a[1].totalSteps)
          .slice(0, topN)

        const entries: LeaderboardEntry[] = await Promise.all(
          sorted.map(async ([uid, b], i) => {
            let displayName = uid
            let photoUrl: string | null = null
            let email: string | null = null
            try {
              const userDoc = await getDoc(doc(db, 'users', uid))
              if (userDoc.exists()) {
                const ud = userDoc.data() as any
                displayName = ud.displayName ?? uid
                photoUrl = ud.photoUrl ?? null
                email = ud.email ?? null
              }
            } catch (e) {
              console.warn('[useStepsLeaderboard] profile fetch failed for', uid, e)
            }
            return {
              uid,
              displayName,
              photoUrl,
              email,
              totalSteps: b.totalSteps,
              daysActive: b.days.size,
              avgSteps: b.days.size > 0 ? Math.round(b.totalSteps / b.days.size) : 0,
              rank: i + 1,
            }
          }),
        )

        if (!cancelled) {
          setData(entries)
          setError(null)
          setLoading(false)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[useStepsLeaderboard] error:', err)
          setError(err instanceof Error ? err : new Error('Aggregation failed'))
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [daysBack, topN])

  return { data, loading, error }
}
